import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ReorderRoundedIcon from "@mui/icons-material/ReorderRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useState } from "react";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import GridViewIcon from "@entrilia/shared/icons/GridViewIcon";
import { ReportTypesEnum } from "../../report-lists/reports/types/reportTypes";
import type { ArtifactsTileSort, ArtifactsView } from "./artifactsListView";

const typeOptions: { type: ReportTypesEnum; label: string }[] = [
  { type: ReportTypesEnum.All, label: "All" },
  { type: ReportTypesEnum.MyReports, label: "Created by Me" },
  { type: ReportTypesEnum.SharedWithMe, label: "Accessible to Me" },
];

const sortOptions: ArtifactsTileSort[] = ["modified", "name"];

const getSortLabel = (sort: ArtifactsTileSort, canManage: boolean): string => {
  if (sort === "name") {
    return "Name";
  }
  return canManage ? "Last modified" : "Last updated";
};

interface TileSortButtonProps {
  sort: ArtifactsTileSort;
  canManage: boolean;
  onChange: (sort: ArtifactsTileSort) => void;
}

const TileSortButton = ({ sort, canManage, onChange }: TileSortButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSelect = (option: ArtifactsTileSort) => {
    onChange(option);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        color="secondary"
        startIcon={<SortRoundedIcon />}
        aria-label={`Sort by ${getSortLabel(sort, canManage)}`}
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {getSortLabel(sort, canManage)}
      </Button>
      <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
        {sortOptions.map((option) => (
          <MenuItem
            key={option}
            role="menuitemradio"
            aria-checked={option === sort}
            onClick={() => handleSelect(option)}
            sx={{ gap: 2 }}
          >
            <ListItemText primary={getSortLabel(option, canManage)} />
            <ListItemIcon sx={{ minWidth: 0, visibility: option === sort ? "visible" : "hidden" }}>
              <CheckRoundedIcon color="primary" />
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface Props {
  canManage: boolean;
  counts: Record<ReportTypesEnum, number>;
  selectedType: ReportTypesEnum;
  searchText: string;
  view: ArtifactsView;
  sort?: ArtifactsTileSort;
  onTypeChange: (type: ReportTypesEnum) => void;
  onSearch: (text: string) => void;
  onViewChange: (view: ArtifactsView) => void;
  onSortChange: (sort: ArtifactsTileSort) => void;
}

const ArtifactsListFilterBar = ({
  canManage,
  counts,
  selectedType,
  searchText,
  view,
  sort,
  onTypeChange,
  onSearch,
  onViewChange,
  onSortChange,
}: Props) => (
  <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", minHeight: 40 }}>
    <ToggleButtonGroup
      value={selectedType}
      exclusive
      aria-label="Show"
      onChange={(_, type: ReportTypesEnum | null) => {
        if (type) {
          onTypeChange(type);
        }
      }}
      sx={{ bgcolor: "background.paper" }}
    >
      {typeOptions.map(({ type, label }) => (
        <ToggleButton key={type} value={type} sx={{ gap: 0.5 }}>
          {label}
          <BadgeDetached showZero badgeContent={counts[type]} color="secondary" />
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
    <Box sx={{ flex: 1 }} />
    <SearchField initialValue={searchText} onSearch={onSearch} debounceTimeMs={300} />
    {sort && <TileSortButton sort={sort} canManage={canManage} onChange={onSortChange} />}
    <ToggleButtonGroup
      value={view}
      exclusive
      color="primary"
      aria-label="View"
      onChange={(_, nextView: ArtifactsView | null) => {
        if (nextView) {
          onViewChange(nextView);
        }
      }}
      sx={{ bgcolor: "background.paper" }}
    >
      <ToggleButton value="tiles" aria-label="Tiles view">
        <GridViewIcon />
      </ToggleButton>
      <ToggleButton value="list" aria-label="List view">
        <ReorderRoundedIcon />
      </ToggleButton>
    </ToggleButtonGroup>
  </Stack>
);

export default ArtifactsListFilterBar;
